import { useCallback, useEffect, useMemo, useState } from 'react';
import { BuildingsIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import { mapFloorPlanOptions } from '../../../../utils/floorPlan';
import { BEDROOM_OPTIONS } from './constants';
import { range } from 'ramda';
import messages from './messages';
// eslint-disable-next-line max-len
import { ProspectProfileFormPreferences } from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useFetchAffordableWaitlist } from '../../../../hooks/data-fetching/useFetchWaitlistV2';

import {
  NO_PREFERENCE_KEY,
  NO_PREFERENCE_TEXT,
} from '../../../../utils/noPreference';

const INITIAL_VALUES = {
  ...ProspectProfileFormPreferences.defaultProps.formState,
};
export const useLookingFor = ({
  intl,
  initialValues,
  floorPlans,
  reduxFormOnChange,
  organizationId,
  propertyId,
  applicationId,
}) => {
  const initialState = {
    ...INITIAL_VALUES,
    ...initialValues,
    nBedsArr: initialValues.nBedsArr,
  };
  const [formState, setFormState] = useState({
    ...initialState,
  });
  const { affordableWaitlistV2 } = useFlags();

  const isNBedsArrPopulated =
    Array.isArray(formState.nBedsArr) && formState.nBedsArr.length > 0;
  const selectedNBeds = formState?.nBedsArr;
  const portalUserHasUnitSelected = initialValues?.unitId;
  const moveInDateFromInitValue = initialValues?.moveInDateFrom;
  const unitIsNotAssigned = !initialValues?.isUnitAssigned;

  const { data: affordableWaitlistData } = useFetchAffordableWaitlist(
    organizationId ?? '',
    propertyId ?? '',
    applicationId ?? '',
    affordableWaitlistV2,
  );

  const handleChange = useCallback(
    ({ name, value }) => {
      const onMoveInDateValueChange =
        name === 'moveInDateFrom' && value !== moveInDateFromInitValue;

      // redux form doesn't change when value = undefined
      if (value === undefined) {
        value = null;
      }

      // once nBedsArr is populated, delete deprecated nBeds
      if (isNBedsArrPopulated) {
        reduxFormOnChange('nBeds', null);
      }

      // sync portal move-in date with fortress move-in date
      if (
        onMoveInDateValueChange &&
        portalUserHasUnitSelected &&
        unitIsNotAssigned
      ) {
        reduxFormOnChange('moveInDateScheduled', value);
      }

      reduxFormOnChange(name, value);
      setFormState((prevState) => ({ ...prevState, [name]: value }));
    },
    [
      reduxFormOnChange,
      isNBedsArrPopulated,
      portalUserHasUnitSelected,
      moveInDateFromInitValue,
      unitIsNotAssigned,
    ],
  );

  const nBedOptions = useMemo(
    () =>
      Array.from(
        new Set(
          floorPlans
            ?.filter((floorPlan) => floorPlan.text !== NO_PREFERENCE_TEXT)
            .map((floorPlan) => floorPlan.nBeds),
        ),
      ),
    [floorPlans],
  );

  const processedFloorPlans = useMemo(() => {
    return mapFloorPlanOptions(floorPlans);
  }, [floorPlans]);

  const floorPlanOptions = useMemo(() => {
    let filteredFloorPlans = processedFloorPlans;

    const { qualifyingPrograms } = affordableWaitlistData?.data || {};

    if (affordableWaitlistV2 && affordableWaitlistData && qualifyingPrograms) {
      const qualifyingProgramsSet = new Set(
        Object.values(qualifyingPrograms)
          .filter((program) => program.passed)
          .map((program) => program.affordableProgramName),
      );
      const qualifyingProgramNames = Array.from(qualifyingProgramsSet);

      filteredFloorPlans = filteredFloorPlans.filter(
        filterByWaitlistQualifyingPrograms,
      );

      function filterByWaitlistQualifyingPrograms(floorPlanOption) {
        const floorPlanPrograms = floorPlanOption.affordablePrograms ?? [];
        return (
          floorPlanPrograms.some((program) =>
            qualifyingProgramNames.includes(program),
          ) || floorPlanOption.nBeds === NO_PREFERENCE_KEY
        );
      }
    }

    return selectedNBeds?.length > 0 && selectedNBeds[0] !== NO_PREFERENCE_KEY
      ? filteredFloorPlans.filter(filterFloorPlansByBedroomSelection)
      : filteredFloorPlans;

    function filterFloorPlansByBedroomSelection(floorPlanOptions) {
      return [...selectedNBeds].some(
        (nBed) =>
          Number(nBed) === floorPlanOptions.nBeds ||
          floorPlanOptions.nBeds === NO_PREFERENCE_KEY,
      );
    }
  }, [
    processedFloorPlans,
    selectedNBeds,
    affordableWaitlistData,
    affordableWaitlistV2,
  ]);

  const leaseTermOptions = useMemo(
    () =>
      range(0, 25).map((x) => {
        const text =
          x === 0
            ? `${intl.formatMessage(messages.chooseOption)}`
            : x === 1
            ? `${x} ${intl.formatMessage(messages.month)}`
            : `${x} ${intl.formatMessage(messages.months)}`;
        return {
          text,
          value: `${x}`,
        };
      }),
    [intl],
  );

  useEffect(() => {
    const chosenFloorPlanNotCompatibleWithSelectedBedroomOptions =
      !floorPlanOptions.find(
        (floorPlan) => floorPlan.value === formState?.preferredFloorPlanId,
      );

    if (chosenFloorPlanNotCompatibleWithSelectedBedroomOptions) {
      setFormState((prevState) => ({
        ...prevState,
        preferredFloorPlanId: null,
      }));
    }
  }, [floorPlanOptions, formState.preferredFloorPlanId]);

  const nBedSelectOptions = useMemo(() => {
    return BEDROOM_OPTIONS.filter((brOption) =>
      nBedOptions.some((nBed) => Number(brOption.value) === nBed),
    );
  }, [nBedOptions]);

  return {
    formState,
    handleChange,
    leaseTermOptions,
    floorPlanOptions,
    nBedSelectOptions,
  };
};

export const useUnitTypeOptions = ({
  propertyClassName,
  hasAffordablePrograms,
  intl,
}) => {
  const allOptions = useMemo(
    () => ({
      affordable: {
        text: intl.formatMessage(messages.affordable),
        icon: <BuildingsIcon fontSize="small" />,
        value: 'affordable',
      },
      conventional: {
        text: intl.formatMessage(messages.conventional),
        icon: <BuildingsIcon fontSize="small" />,
        value: 'conventional',
      },
    }),
    [intl],
  );

  const unitTypeOptionMap = {
    Mixed: ['affordable', 'conventional'],
    Conventional: ['conventional'],
    default: ['affordable'],
  };

  const selectedKeys =
    unitTypeOptionMap[propertyClassName ?? ''] ?? unitTypeOptionMap.default;

  const keys =
    hasAffordablePrograms && !selectedKeys.includes('affordable')
      ? ['affordable', ...selectedKeys]
      : selectedKeys;

  return keys.map((key) => allOptions[key]);
};
